/**
 * Geometry helpers for the guidance screen: a rough distance and compass
 * direction from a reference point (the volunteer's live position, or the
 * session's `start_point` when it is unknown) to a door's address.
 *
 * There is no device-heading API reliable enough across browsers to give a
 * "turn left / turn right" instruction, so direction is an 8-point compass
 * label (Nord, Nord-Est, …) rather than the prototype's illustrative
 * "↗ à droite" — a deliberate simplification, not a contract gap.
 */
import type { GeoPoint } from '../../../api/sessions';

const EARTH_RADIUS_M = 6_371_000;

/** Degrees to radians. */
function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Great-circle distance between two WGS 84 points.
 *
 * @param from - The starting point.
 * @param to - The destination point.
 * @returns The distance in metres, rounded to the nearest metre.
 */
export function distanceMetres(from: GeoPoint, to: GeoPoint): number {
  const dLat = toRadians(to.latitude - from.latitude);
  const dLon = toRadians(to.longitude - from.longitude);
  const lat1 = toRadians(from.latitude);
  const lat2 = toRadians(to.latitude);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  return Math.round(2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h))));
}

/**
 * Compass bearing from one point to another.
 *
 * @param from - The starting point.
 * @param to - The destination point.
 * @returns The bearing in degrees, `0` = north, clockwise.
 */
export function bearingDegrees(from: GeoPoint, to: GeoPoint): number {
  const lat1 = toRadians(from.latitude);
  const lat2 = toRadians(to.latitude);
  const dLon = toRadians(to.longitude - from.longitude);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const theta = Math.atan2(y, x);
  return ((theta * 180) / Math.PI + 360) % 360;
}

/** The eight compass points, in French, index 0 = north, clockwise. */
const COMPASS_POINTS = [
  'Nord',
  'Nord-Est',
  'Est',
  'Sud-Est',
  'Sud',
  'Sud-Ouest',
  'Ouest',
  'Nord-Ouest',
] as const;

/**
 * A rough 8-point compass label for a bearing.
 *
 * @param bearing - Degrees, `0` = north, clockwise.
 * @returns A French compass label, e.g. `"Nord-Est"`.
 */
export function compassLabel(bearing: number): string {
  const index = Math.round(bearing / 45) % COMPASS_POINTS.length;
  return COMPASS_POINTS[index] ?? 'Nord';
}
